import React, { useState, useEffect } from 'react';
import { Routes, Route, useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { patientBookingAPI, patientAccessAPI, ConnectedClinician } from '../services/api';
import { LoadingSpinner } from './common/LoadingSpinner';
import DashboardLayout from './DashboardLayout';
import BookingCalendar from './patient/BookingCalendar';
import MyBookings from './patient/MyBookings';
import MyClinicians from './patient/MyClinicians';
import { BookingListItem } from '../types/booking';
import Card, { CardHeader } from './ui/Card';
import Button from './ui/Button';
import { WelcomeBanner } from './ui/StatusBanner';
import {
  CalendarIcon,
  UserIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  TicketIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { apiErrorMessage } from '../utils/apiError';

const PatientDashboard: React.FC = () => {
  return (
    <DashboardLayout>
      <Routes>
        <Route path="/" element={<PatientHome />} />
        <Route path="/book" element={<BookAppointment />} />
        <Route path="/book/:slug" element={<BookAppointment />} />
        <Route path="/appointments" element={<MyAppointments />} />
        <Route path="/my-clinicians" element={<MyClinicians />} />
        <Route path="/profile" element={<PatientProfile />} />
      </Routes>
    </DashboardLayout>
  );
};

const PatientHome: React.FC = () => {
  const { user } = useAuth();
  const [clinician, setClinician] = useState<any | null>(null);
  const [bookings, setBookings] = useState<BookingListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load clinician info
        try {
          const clinicianData = await patientBookingAPI.getMyClinician();
          setClinician(clinicianData);
        } catch (err: any) {
          // Not an error if no clinician - that's expected for new patients
          if (err.response?.status !== 404) {
            console.error('Failed to load clinician:', err);
          }
        }

        // Load bookings for stats
        try {
          const bookingsData = await patientBookingAPI.getMyBookings();
          setBookings(bookingsData);
        } catch (err: any) {
          console.error('Failed to load bookings:', err);
        }
      } catch (error: any) {
        setError(apiErrorMessage(error, 'Failed to load data'));
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.role === 'patient') {
      loadData();
    } else {
      setError('Access denied: Patient role required');
      setIsLoading(false);
    }
  }, [user]);

  // Calculate stats from bookings
  const now = new Date();
  const upcomingBookings = bookings.filter(
    b => new Date(b.start_time) > now && b.status.toLowerCase() !== 'cancelled'
  );
  const nextAppointment = upcomingBookings.length > 0
    ? upcomingBookings.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())[0]
    : null;
  const totalAppointments = bookings.filter(b => b.status.toLowerCase() !== 'cancelled').length;

  const formatNextAppointment = () => {
    if (!nextAppointment) return '--';
    const date = new Date(nextAppointment.start_time);
    return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-clinic-dark font-body">
          Welcome back{user?.first_name ? `, ${user.first_name}` : ''}
        </h1>
        <p className="mt-1 text-clinic-muted">
          Manage your appointments and connect with your clinicians.
        </p>
      </div>

      {/* Welcome Banner for new patients without connected clinicians */}
      {!clinician && !error && (
        <WelcomeBanner
          userName={user?.first_name}
          primaryAction={
            <Button
              as={Link}
              to="/clinicians"
              leftIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
            >
              Browse Directory
            </Button>
          }
          secondaryAction={
            <Button
              variant="secondary"
              as={Link}
              to="/patient/my-clinicians"
              leftIcon={<TicketIcon className="h-5 w-5" />}
            >
              Enter Invite Code
            </Button>
          }
        />
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card variant="default" padding="md">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-primary/10 rounded-full flex items-center justify-center">
              <CalendarIcon className="h-6 w-6 text-clinic-primary" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">Next Appointment</p>
              <p className="text-lg font-semibold text-clinic-dark">{formatNextAppointment()}</p>
            </div>
          </div>
        </Card>

        <Link to="/patient/my-clinicians">
          <Card variant="default" padding="md" className="hover:border-clinic-primary transition-colors cursor-pointer">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-clinic-primary/10 rounded-full flex items-center justify-center">
                <UserIcon className="h-6 w-6 text-clinic-primary" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-clinic-muted">My Clinicians</p>
                <p className="text-lg font-semibold text-clinic-dark">
                  {clinician ? clinician.name : 'View connections'}
                </p>
              </div>
            </div>
          </Card>
        </Link>

        <Card variant="default" padding="md">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-gold/10 rounded-full flex items-center justify-center">
              <ClockIcon className="h-6 w-6 text-clinic-gold" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-clinic-muted">Total Appointments</p>
              <p className="text-lg font-semibold text-clinic-dark">{totalAppointments}</p>
            </div>
          </div>
        </Card>
      </div>


      {/* Quick Actions */}
      <Card variant="default" padding="md">
        <CardHeader title="Quick Actions" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Link
            to="/patient/book"
            className="group flex items-center p-4 rounded-xl border border-clinic-primary-lighter hover:border-clinic-primary hover:bg-clinic-primary/5 transition-all"
          >
            <div className="w-10 h-10 bg-clinic-primary/10 rounded-lg flex items-center justify-center group-hover:bg-clinic-primary/20 transition-colors">
              <CalendarIcon className="h-5 w-5 text-clinic-primary" />
            </div>
            <div className="ml-3">
              <h3 className="font-medium text-clinic-dark">Book Appointment</h3>
              <p className="text-xs text-clinic-muted">Schedule a new appointment</p>
            </div>
          </Link>

          <Link
            to="/patient/appointments"
            className="group flex items-center p-4 rounded-xl border border-clinic-primary-lighter hover:border-clinic-primary hover:bg-clinic-primary/5 transition-all"
          >
            <div className="w-10 h-10 bg-clinic-primary/10 rounded-lg flex items-center justify-center group-hover:bg-clinic-primary/20 transition-colors">
              <ClockIcon className="h-5 w-5 text-clinic-primary" />
            </div>
            <div className="ml-3">
              <h3 className="font-medium text-clinic-dark">My Appointments</h3>
              <p className="text-xs text-clinic-muted">View upcoming & past</p>
            </div>
          </Link>

          <Link
            to="/patient/my-clinicians"
            className="group flex items-center p-4 rounded-xl border border-clinic-primary-lighter hover:border-clinic-primary hover:bg-clinic-primary/5 transition-all"
          >
            <div className="w-10 h-10 bg-clinic-gold/10 rounded-lg flex items-center justify-center group-hover:bg-clinic-gold/20 transition-colors">
              <UserGroupIcon className="h-5 w-5 text-clinic-gold" />
            </div>
            <div className="ml-3">
              <h3 className="font-medium text-clinic-dark">My Clinicians</h3>
              <p className="text-xs text-clinic-muted">Manage connections</p>
            </div>
          </Link>
        </div>
      </Card>
    </div>
  );
};

/**
 * BookAppointment Component
 *
 * Three-mode booking flow:
 * 1. URL slug provided: Show that clinician's availability
 * 2. No slug + has connections: Show clinician picker grid
 * 3. No slug + no connections: Show welcome message with directory/invite CTAs
 */
const BookAppointment: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();

  const [clinician, setClinician] = useState<any | null>(null);
  const [connectedClinicians, setConnectedClinicians] = useState<ConnectedClinician[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (slug) {
          const connectedClinician = await patientAccessAPI.getConnectedClinicianBySlug(slug);
          setClinician({
            id: connectedClinician.clinician_id,
            name: connectedClinician.clinician_name,
            email: '',
            specialty: connectedClinician.clinician_specialty,
            slug: connectedClinician.booking_slug,
            requires_booking_approval: connectedClinician.requires_booking_approval,
            is_public: connectedClinician.is_public,
            booking_mode: connectedClinician.booking_mode,
            services: connectedClinician.services,
          });
        } else {
          const response = await patientAccessAPI.getMyClinicians();
          setConnectedClinicians(response.items);
        }
      } catch (err: any) {
        const detail = apiErrorMessage(err, 'Failed to load data');
        if (err.response?.status === 404 && slug) {
          setError('Clinician not found. The booking link may be invalid or expired.');
        } else if (err.response?.status === 403) {
          setError('You do not have access to book with this clinician. Please use an invite code to connect first.');
        } else if (!slug) {
          setConnectedClinicians([]);
        } else {
          setError(detail);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [slug]);

  const handleSelectClinician = (clinicianSlug: string) => {
    navigate(`/patient/book/${clinicianSlug}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-clinic-dark">Book Appointment</h1>
        <Card variant="outlined" padding="lg">
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-clinic-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CalendarIcon className="h-8 w-8 text-clinic-accent" />
            </div>
            <h2 className="text-lg font-semibold text-clinic-dark mb-2">Unable to Load</h2>
            <p className="text-clinic-muted mb-6">{error}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button as={Link} to="/clinicians" leftIcon={<MagnifyingGlassIcon className="h-4 w-4" />}>
                Browse Directory
              </Button>
              <Button variant="secondary" as={Link} to="/patient/my-clinicians" leftIcon={<UserGroupIcon className="h-4 w-4" />}>
                My Clinicians
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Mode 1: Slug provided → show clinician's availability
  if (slug && clinician) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-clinic-dark">Book Appointment</h1>
          <p className="text-clinic-muted mt-1">
            with <span className="font-medium text-clinic-primary">{clinician.name}</span>
          </p>
        </div>
        <BookingCalendar clinician={clinician} />
      </div>
    );
  }

  // Mode 2: Has connected clinicians → show picker grid
  if (connectedClinicians.length > 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-clinic-dark">Book Appointment</h1>
          <p className="text-clinic-muted mt-1">Select a clinician to view their availability</p>
        </div>

        {/* Clinician Picker Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {connectedClinicians.map((c) => (
            <Card
              key={c.clinician_id}
              variant="default"
              padding="md"
              hover
              onClick={() => c.booking_slug && c.can_book && handleSelectClinician(c.booking_slug)}
              className={!c.booking_slug || !c.can_book ? 'opacity-50 cursor-not-allowed' : ''}
            >
              <div className="flex items-start space-x-3">
                {/* Avatar */}
                <div className="w-12 h-12 bg-clinic-primary/10 rounded-full flex items-center justify-center font-semibold text-clinic-primary flex-shrink-0">
                  {c.clinician_name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-clinic-dark truncate">{c.clinician_name}</h3>
                  {c.clinician_specialty && (
                    <p className="text-sm text-clinic-muted truncate">{c.clinician_specialty}</p>
                  )}
                  {c.requires_booking_approval && (
                    <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-clinic-gold/20 text-clinic-gold">
                      Requires approval
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-clinic-primary-lighter">
                <span className="text-sm font-medium text-clinic-primary">
                  View Availability →
                </span>
              </div>
            </Card>
          ))}
        </div>

        {/* Additional Options */}
        <div className="border-t border-clinic-primary-lighter pt-6">
          <p className="text-sm text-clinic-muted text-center mb-4">
            Looking for someone else?
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button variant="secondary" as={Link} to="/clinicians" leftIcon={<MagnifyingGlassIcon className="h-4 w-4" />}>
              Browse Directory
            </Button>
            <Button variant="secondary" as={Link} to="/patient/my-clinicians" leftIcon={<TicketIcon className="h-4 w-4" />}>
              Enter Invite Code
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Mode 3: No connections → welcome message for new patients
  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-clinic-dark">Book Appointment</h1>
      <WelcomeBanner
        primaryAction={
          <Button as={Link} to="/clinicians" leftIcon={<MagnifyingGlassIcon className="h-5 w-5" />}>
            Browse Directory
          </Button>
        }
        secondaryAction={
          <Button variant="secondary" as={Link} to="/patient/my-clinicians" leftIcon={<TicketIcon className="h-5 w-5" />}>
            Enter Invite Code
          </Button>
        }
      />
    </div>
  );
};

const MyAppointments: React.FC = () => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-clinic-dark">My Appointments</h1>
        <p className="text-clinic-muted mt-1">View and manage your upcoming and past appointments.</p>
      </div>
      <MyBookings />
    </div>
  );
};

const PatientProfile: React.FC = () => {
  const { user } = useAuth();

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-clinic-dark">My Account</h1>
        <p className="text-clinic-muted mt-1">View your account information.</p>
      </div>

      <Card variant="default" padding="lg">
        <div className="space-y-6">
          {/* Profile Header */}
          <div className="flex items-center space-x-4 pb-6 border-b border-clinic-primary-lighter">
            <div className="w-16 h-16 bg-clinic-primary rounded-full flex items-center justify-center">
              <span className="text-clinic-cream font-semibold text-xl">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-clinic-dark">
                {user?.first_name} {user?.last_name}
              </h2>
              <p className="text-clinic-muted">Patient Account</p>
            </div>
          </div>

          {/* Account Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-clinic-muted mb-1">
                First Name
              </label>
              <p className="text-clinic-dark">{user?.first_name || 'Not set'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-clinic-muted mb-1">
                Last Name
              </label>
              <p className="text-clinic-dark">{user?.last_name || 'Not set'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-clinic-muted mb-1">
                Email Address
              </label>
              <p className="text-clinic-dark">{user?.email || 'Not set'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-clinic-muted mb-1">
                Phone Number
              </label>
              <p className="text-clinic-dark">{user?.phone || 'Not set'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-clinic-muted mb-1">
                Member Since
              </label>
              <p className="text-clinic-dark">{formatDate(user?.created_at)}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-clinic-muted mb-1">
                Account Status
              </label>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                user?.is_active
                  ? 'bg-clinic-primary/20 text-clinic-primary'
                  : 'bg-clinic-accent/20 text-clinic-accent'
              }`}>
                {user?.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Help Text */}
          <div className="pt-6 border-t border-clinic-primary-lighter">
            <p className="text-sm text-clinic-muted">
              Need to update your information? Please contact your clinician or clinic support for assistance.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PatientDashboard;
